import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { FiBarChart2, FiChevronDown } from 'react-icons/fi';
import { WidgetDef, SettingValues, GENERATE_NOTE_SINGLE } from '../types';
import { DemoHead, useWidgetSite } from '../shared';

// spec 628:5661
// "More colours" (a custom control, so settingDefaults doesn't seed them): unset = these, blank = fall back to the two main colours
const MORE_COLOURS: { key: string; label: string; default: string; fallback: 'chart' | 'text' | 'none' }[] = [
    { key: 'areaColor', label: 'Area under the line · fade to clear', default: '#00B5D1', fallback: 'chart' },
    { key: 'gridColor', label: 'Grid lines', default: '#E5E9F0', fallback: 'none' },
    { key: 'axisColor', label: 'Axis line', default: '#D6DCE6', fallback: 'none' },
    { key: 'markerColor', label: 'Hover dot', default: '#0B1220', fallback: 'text' },
    { key: 'crosshairColor', label: 'Crosshair line', default: '#94A3B8', fallback: 'text' },
    { key: 'tooltipBg', label: 'Tooltip background', default: '#0B1220', fallback: 'text' },
    { key: 'tooltipText', label: 'Tooltip text', default: '#FFFFFF', fallback: 'none' },
    { key: 'background', label: 'Chart background', default: 'Transparent', fallback: 'none' },
];

const isHex = (v: string) => /^#[0-9a-f]{6}$/i.test(v);
const rgba = (hex: string, a: number) => isHex(hex)
    ? `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${a})`
    : hex;

// the colour the chart draws with: the setting, its default when unset, the main colour (or a neutral) when blank
const colours = (s: SettingValues) => {
    const chart = String(s.chartColor || '#00B5D1');
    const text = String(s.textColor || '#0B1220');
    const out: Record<string, string> = { chart, text };
    MORE_COLOURS.forEach(c => {
        const v = s[c.key] === undefined ? c.default : String(s[c.key]).trim();
        if (v) out[c.key] = v;
        else if (c.fallback === 'chart') out[c.key] = chart;
        else if (c.fallback === 'text') out[c.key] = text;
        else out[c.key] = c.default;
    });
    if (out.background.toLowerCase() === 'transparent') out.background = 'transparent';
    return out;
};

const MoreColours: React.FC<{ settings: SettingValues; set: (key: string, value: string | boolean) => void }> = ({ settings, set }) => {
    const [open, setOpen] = useState(true);
    return <div className="wd-more-colours">
        <button type="button" className={`head${open ? '' : ' closed'}`} aria-expanded={open} onClick={() => setOpen(!open)}>
            <span className="wd-label">More colours</span> <FiChevronDown className="chev" />
        </button>
        {open && <>
            <div className="grid">
                {MORE_COLOURS.map(c => {
                    const value = settings[c.key] === undefined ? c.default : String(settings[c.key]);
                    return <label key={c.key} className="item">
                        <small>{c.label}</small>
                        <span className="box">
                            <input type="color" value={isHex(value) ? value : '#ffffff'} className={isHex(value) ? '' : 'empty'}
                                onChange={e => set(c.key, e.target.value.toUpperCase())} aria-label={c.label} />
                            <input className="hex" value={value} onChange={e => set(c.key, e.target.value)} />
                        </span>
                    </label>;
                })}
            </div>
            <p className="wd-muted">Leave any of these blank and the chart uses your two main colours. “Transparent” lets the page background show through.</p>
        </>}
    </div>;
};

// sample prices ending at A$0.245 on 2 Sep 2026. 6m is the line as drawn in the spec; the rest are a made-up walk.
const END = '2026-09-02';
const SPEC_LINE = [0.212, 0.215, 0.210, 0.218, 0.222, 0.219, 0.225, 0.230, 0.228, 0.226, 0.233, 0.238, 0.235, 0.240, 0.244, 0.239, 0.236, 0.242,
    0.250, 0.262, 0.258, 0.252, 0.248, 0.245, 0.243, 0.240, 0.238, 0.236, 0.245];
const RANGES = ['1m', '3m', '6m', 'YTD', '1y', '2y', 'All'];
const WALKS: Record<string, { points: number; stepDays: number; drift: number }> = {
    '1m': { points: 22, stepDays: 1, drift: 0.02 },
    '3m': { points: 46, stepDays: 2, drift: 0.04 },
    'YTD': { points: 35, stepDays: 7, drift: 0.06 },
    '1y': { points: 52, stepDays: 7, drift: 0.09 },
    '2y': { points: 52, stepDays: 14, drift: 0.05 },
    'All': { points: 72, stepDays: 30, drift: 0.15 },
};

const series = (range: string) => {
    if (range === '6m') return SPEC_LINE.map((v, i) => ({ date: moment(END).subtract(SPEC_LINE.length - 1 - i, 'days'), v }));
    const w = WALKS[range];
    const raw = Array.from({ length: w.points }, (_, i) => -w.drift * (1 - i / (w.points - 1)) + 0.012 * Math.sin(i * 0.7 + w.points) + 0.006 * Math.sin(i * 1.9));
    const shift = 0.245 - raw[raw.length - 1];
    return raw.map((r, i) => ({ date: moment(END).subtract((w.points - 1 - i) * w.stepDays, 'days'), v: Math.max(0.05, r + shift) }));
};

const STATS = [['Open', 'A$0.235'], ['High', 'A$0.250'], ['Low', 'A$0.232'], ['Volume', '659K'], ['52-wk range', 'A$0.11 – 0.31'], ['Market cap', 'A$31.5M']];

const Demo: React.FC<{ settings: SettingValues }> = ({ settings }) => {
    const { ticker } = useWidgetSite();
    const [range, setRange] = useState('6m');
    const chartRef = useRef<ReactECharts>(null);
    const c = colours(settings);
    const pts = series(range);
    const short = WALKS[range] ? WALKS[range].stepDays <= 2 : true;
    const label = (i: number) => pts[i].date.format(short ? 'D MMM' : 'MMM YY');

    const option = {
        animation: false,
        backgroundColor: c.background,
        grid: { left: 40, right: 14, top: 12, bottom: 28 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: c.tooltipBg, borderWidth: 0, padding: [7, 10],
            textStyle: { color: c.tooltipText, fontSize: 12 },
            axisPointer: { type: 'line', lineStyle: { color: c.crosshairColor, type: 'dashed' } },
            formatter: (params: { dataIndex: number }[]) => {
                const i = params[0].dataIndex;
                const vol = i === pts.length - 1 ? 659 : 420 + (i * 53) % 380;
                return `<strong>${pts[i].date.format('D MMM')} · A$${pts[i].v.toFixed(3)}</strong><br/><span style="opacity:0.7;font-size:11px">Vol ${vol}K</span>`;
            },
        },
        xAxis: {
            type: 'category', boundaryGap: false, data: pts.map((_, i) => label(i)),
            axisLine: { lineStyle: { color: c.axisColor } }, axisTick: { show: false },
            axisLabel: { color: rgba(c.text, 0.6), fontSize: 11, interval: Math.ceil(pts.length / 5) - 1 },
        },
        yAxis: {
            type: 'value', scale: true,
            splitLine: { lineStyle: { color: c.gridColor, type: 'dashed' } },
            axisLabel: { color: rgba(c.text, 0.6), fontSize: 11, formatter: (v: number) => v.toFixed(2) },
        },
        series: [{
            type: 'line', data: pts.map(p => p.v), showSymbol: false, symbol: 'circle', symbolSize: 9,
            lineStyle: { color: c.chart, width: 2 },
            itemStyle: { color: c.markerColor, borderColor: '#fff', borderWidth: 2 },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: rgba(c.areaColor, 0.35) }, { offset: 1, color: rgba(c.areaColor, 0) }] } },
        }],
    };

    // the spec shows the tooltip on the latest price; re-pin it after every redraw
    useEffect(() => {
        chartRef.current?.getEchartsInstance().dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: pts.length - 1 });
    });

    return <div className="wd-chart">
        <DemoHead title="Share price" sub={`ASX: ${ticker} · 20 minute delay`} />
        <div className="top">
            <strong>A$0.245</strong>
            <span className="chip">+4.3% (+A$0.010)</span>
            <div className="ranges" role="radiogroup" aria-label="Range">
                {RANGES.map(r => <button key={r} type="button" role="radio" aria-checked={r === range} onClick={() => setRange(r)}
                    style={r === range ? { background: c.text } : undefined} className={r === range ? 'active' : ''}>{r}</button>)}
            </div>
        </div>
        <ReactECharts ref={chartRef} option={option} notMerge={true} style={{ height: 220 }} />
        <div className="wd-chart-stats">
            <div className="head" />
            {STATS.map(([l, v]) => <div key={l} className="row"><span>{l}</span><span className="v">{v}</span></div>)}
        </div>
        <p className="muted small">Same chart as your Investi dashboard · prices delayed 20 minutes, source ASX</p>
    </div>;
};

const sharePriceChart: WidgetDef = {
    slug: 'share-price-chart',
    name: 'Share price chart',
    description: 'The 30‑day chart, same style as your dashboard',
    icon: <FiBarChart2 />,
    title: 'Share Price Chart Widget',
    installed: true,
    page: '/investors/investors-dashboard/',
    views: 1240,
    demoPath: '/investors/investors-dashboard/',
    addNote: 'This code is generated from the settings below. Change a setting and the code updates — paste it once, it stays current.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_share_price_chart range=6m instance="share-price-chart"]' },
        {
            title: '2 · Or plain HTML',
            code: '<div data-investi-instance="share-price-chart" class="investi-share-price-chart"\n     data-investi-chart-theme-color-hex="#00B5D1"\n     data-investi-chart-text-color-hex="#0B1220"></div>',
            dark: true,
        },
        {
            title: '3 · Optional attributes',
            code: [
                'data-investi-chart-range-selected="2"',
                'data-investi-range-selector-buttons="1m,3m,6m,YTD,1y,2y,All"',
                'data-investi-chart-area-color-hex="#00B5D1"',
                'data-investi-chart-area-fade="true"',
                'data-investi-chart-split-line-color-hex="#E5E9F0"',
                'data-investi-chart-axis-line-color-hex="#D6DCE6"',
                'data-investi-chart-marker-color-hex="#0B1220"',
                'data-investi-chart-crosshair-color-hex="#94A3B8"',
                'data-investi-chart-tooltip-bg-hex="#0B1220"',
                'data-investi-chart-tooltip-text-hex="#FFFFFF"',
                'data-investi-chart-background-hex="transparent"',
            ].join('\n'),
            dark: true,
            after: '‘Selected’ counts from 0, so 2 opens the chart on 6m. Every colour attribute is optional — leave it out and the chart falls back to the two main colours.',
        },
    ],
    addFooter: 'The Investi stylesheet, script and chart library are already on your website — added once in step 1, Connect.',
    settings: [
        { kind: 'color', key: 'chartColor', label: 'Chart colour', hint: 'The line, the fill under it and the range buttons.', default: '#00B5D1' },
        { kind: 'color', key: 'textColor', label: 'Text colour inside the chart', hint: 'Axis labels, the dates and the tooltip. Pick something that reads clearly on your page background.', default: '#0B1220' },
        { kind: 'note', text: 'Everything else — which range buttons appear and which one it opens on — is set with the optional attributes above.' },
        { kind: 'custom', render: MoreColours },
    ],
    generateNote: GENERATE_NOTE_SINGLE,
    Demo,
};

export default sharePriceChart;
